import React, { useEffect, useState } from "react"
import DataTable from "aldoo-ra/DataTable"
import Button from "aldoo-ra/Button"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Typography from "aldoo-ra/Typography"
import Modal from "aldoo-ra/Modal"
import Tooltip from "aldoo-ra/Tooltip"
import { showNotification } from "aldoo-ra/Notification"
import {
  PlusCircleIcon,
  ClipboardIcon,
  ChevronRightIcon,
  ChevronDownIcon
} from "@heroicons/react/24/outline"
import { GetParams, DeleteParam, GetValueType, FormatValue, GetAuthors } from "./api"
import ParamEditDialog from "./components/ParamEditDialog"

const getSearchFromUrl = () => {
  const params = new URLSearchParams(window.location.search)
  return params.get("search") || ""
}

const ParamBrowser = () => {
  const [selectedParam, setSelectedParam] = useState(null)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [filterAuthor, setFilterAuthor] = useState([
    { value: null, label: "All Authors" }
  ])
  const [availableAuthors, setAvailableAuthors] = useState([])
  const [expandedGroups, setExpandedGroups] = useState(new Set())
  const [searchTerm, setSearchTerm] = useState(getSearchFromUrl)


  // Load available authors on component mount and when data changes
  useEffect(() => {
    const loadAuthors = async () => {
      try {
        const response = await GetAuthors()
        if (response.result && response.data) {
          const authorOptions = [
            { value: null, label: "All Authors" },
            ...response.data.map(author => ({
              value: author,
              label: author
            }))
          ]
          setAvailableAuthors(authorOptions)
        }
      } catch (error) {
        console.error("Failed to load authors:", error)
      }
    }

    loadAuthors()
  }, [refreshKey])

  // Sync search term with URL
  useEffect(() => {
    const handlePopState = () => {
      setSearchTerm(getSearchFromUrl())
    }
    window.addEventListener("popstate", handlePopState)
    return () => window.removeEventListener("popstate", handlePopState)
  }, [])

  const handleSearchChange = (value) => {
    setSearchTerm(value)
    const currentParams = new URLSearchParams(window.location.search)
    if (value && value.trim()) {
      currentParams.set("search", value)
    } else {
      currentParams.delete("search")
    }
    const queryString = currentParams.toString()
    const newUrl = window.location.pathname + (queryString ? `?${queryString}` : "")
    if (window.location.href !== newUrl) {
      window.history.pushState({}, "", newUrl)
    }
  }

  // Build match object for API filters
  const getRequestFields = () => {
    const match = {}

    // Author filtering
    const selectedAuthor = filterAuthor[0]?.value
    if (selectedAuthor) {
      match.author = selectedAuthor
    }

    return { match }
  }

  // Handle parameter actions
  const handleEdit = (param) => {
    setSelectedParam(param)
    setShowEditDialog(true)
  }



  const confirmDelete = async () => {
    if (!selectedParam) return

    try {
      const response = await DeleteParam({ address: selectedParam.address })
      if (response.result) {
        setShowDeleteConfirm(false)
        setSelectedParam(null)
        setRefreshKey(prev => prev + 1) // Trigger refresh
      }
    } catch (err) {
      console.error("Error deleting parameter:", err)
    }
  }

  // Handle dialog closes and refreshes
  const handleDialogClose = (shouldRefresh = false) => {
    setShowCreateDialog(false)
    setShowEditDialog(false)
    setSelectedParam(null)

    if (shouldRefresh) {
      setRefreshKey(prev => prev + 1) // Trigger refresh
    }
  }

  // Refresh DataTable without closing dialogs
  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1)
  }

  // Update author filter with proper array handling
  const updateAuthorFilter = (newFilter) => {
    const filterArray = newFilter?.length
      ? [newFilter[0]]
      : [{ value: null, label: "All Authors" }]

    setFilterAuthor(filterArray)
    setRefreshKey(prev => prev + 1)
  }

  // Helper function to get unique historical authors
  const getHistoricalAuthors = (param) => {
    if (!param.history || !Array.isArray(param.history)) {
      return []
    }

    // Get unique authors from history, excluding the current author
    const currentAuthor = param.author || "Unknown"
    const uniqueAuthors = [...new Set(
      param.history
        .map(entry => entry.author)
        .filter(author => author && author !== currentAuthor)
    )]

    return uniqueAuthors
  }

  // Toggle group expand state
  const toggleGroup = (groupPath) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev)
      if (newSet.has(groupPath)) {
        newSet.delete(groupPath)
      } else {
        newSet.add(groupPath)
      }
      return newSet
    })
  }

  // Build hierarchical data structure
  const buildHierarchicalData = (params, searchTerm = "") => {
    if (!params || !Array.isArray(params)) return []

    const result = []
    const groupMap = new Map()
    const paramPaths = new Set(params.map(p => p.address))
    
    // Filter parameters based on search
    let filteredParams = params
    if (searchTerm && searchTerm.trim()) {
      const search = searchTerm.toLowerCase()
      filteredParams = params.filter(param => 
        param.address.toLowerCase().includes(search) ||
        (param.description && param.description.toLowerCase().includes(search)) ||
        JSON.stringify(param.value).toLowerCase().includes(search)
      )
    }

    // If searching, show only matching items without grouping
    if (searchTerm && searchTerm.trim()) {
      return filteredParams.map(param => ({
        type: 'parameter',
        param,
        level: 0,
        displayAddress: param.address
      }))
    }

    // First pass: organize parameters by namespace and identify which paths are actual parameters vs groups
    params.forEach(param => {
      const parts = param.address.split('.')
      
      if (parts.length === 1) {
        // No namespace, add directly to root
        result.push({
          type: 'parameter',
          param,
          level: 0,
          displayAddress: param.address
        })
      } else {
        // Has namespace - build nested structure
        let currentPath = ''
        
        // Create all namespace levels
        for (let i = 0; i < parts.length - 1; i++) {
          const part = parts[i]
          const parentPath = currentPath
          currentPath = currentPath ? `${currentPath}.${part}` : part
          
          if (!groupMap.has(currentPath)) {
            groupMap.set(currentPath, {
              name: part,
              fullPath: currentPath,
              parentPath: parentPath,
              level: i,
              parameters: [],
              children: new Set()
            })
          }
          
          // Track parent-child relationships
          if (parentPath && groupMap.has(parentPath)) {
            groupMap.get(parentPath).children.add(currentPath)
          }
        }
        
        // Determine if this parameter should be added to a group or treated as a standalone parameter
        const parentPath = parts.slice(0, -1).join('.')
        const currentParamPath = param.address
        
        // Check if this parameter path is actually a parent to other parameters
        const isParentPath = Array.from(paramPaths).some(path => 
          path !== currentParamPath && path.startsWith(currentParamPath + '.')
        )
        
        if (isParentPath) {
          // This parameter is actually a namespace - don't add it as a parameter
          // Create a group for it if it doesn't exist
          if (!groupMap.has(currentParamPath)) {
            const paramParts = currentParamPath.split('.')
            const paramParentPath = paramParts.slice(0, -1).join('.')
            
            groupMap.set(currentParamPath, {
              name: paramParts[paramParts.length - 1],
              fullPath: currentParamPath,
              parentPath: paramParentPath,
              level: paramParts.length - 1,
              parameters: [],
              children: new Set()
            })
            
            // Update parent's children
            if (paramParentPath && groupMap.has(paramParentPath)) {
              groupMap.get(paramParentPath).children.add(currentParamPath)
            }
          }
        } else {
          // Regular parameter - add to its parent group
          if (groupMap.has(parentPath)) {
            groupMap.get(parentPath).parameters.push({
              ...param,
              displayAddress: parts[parts.length - 1] // Just the final part
            })
          }
        }
      }
    })

    // Second pass: flatten into renderable array
    const flattenGroup = (groupPath, level = 0) => {
      const items = []
      const group = groupMap.get(groupPath)
      
      if (!group) return items
      
      // Count total parameters in this group and all children
      const countParams = (path) => {
        const g = groupMap.get(path)
        if (!g) return 0
        let count = g.parameters.length
        g.children.forEach(childPath => {
          count += countParams(childPath)
        })
        return count
      }
      
      // Add group header (collapsed by default)
      // Logic: a group is expanded only if it's explicitly in the expandedGroups set
      const isExpanded = expandedGroups.has(group.fullPath)
      const isCollapsed = !isExpanded
      const shouldShowContent = isExpanded
      
      const groupItem = {
        type: 'group',
        name: group.name,
        fullPath: group.fullPath,
        level,
        isCollapsed,
        paramCount: countParams(group.fullPath)
      }
      items.push(groupItem)
      
      // Add content if group is expanded
      if (shouldShowContent) {
        // Add child groups first
        Array.from(group.children).sort().forEach(childPath => {
          items.push(...flattenGroup(childPath, level + 1))
        })
        
        // Add direct parameters
        group.parameters.forEach(param => {
          items.push({
            type: 'parameter',
            param,
            level: level + 1,
            groupPath: group.fullPath,
            displayAddress: param.displayAddress || param.address
          })
        })
      }
      
      return items
    }

    // Add root-level groups
    const rootGroups = Array.from(groupMap.keys()).filter(path => !path.includes('.'))
    rootGroups.sort().forEach(groupPath => {
      result.push(...flattenGroup(groupPath))
    })

    return result
  }

  // Handle copying value to clipboard
  const handleCopyValue = async (param) => {
    try {
      const valueText = FormatValue(param.value)
      await navigator.clipboard.writeText(valueText)
      
      showNotification({
        message: (
          <div className="flex items-center space-x-2">
            <ClipboardIcon className="w-4 h-4" />
            <span>Value copied to clipboard</span>
          </div>
        ),
        className: "bg-admin_success text-white",
        duration: 2000
      })
    } catch (err) {
      showNotification({
        message: "Failed to copy value to clipboard",
        className: "bg-admin_error text-white",
        duration: 3000
      })
    }
  }

  // Header controls for DataTable
  const headerControls = (
    <div className="w-full flex items-end">
      {/* Author filter on the left */}
      <div className="flex flex-col">
        <Typography 
          variant="admin_txt2_dim" 
          className="mb-0.5"
        >
          Filter by Author
        </Typography>

        <DropdownSelect
          variant="admin"
          options={availableAuthors}
          value={filterAuthor}
          onChange={updateAuthorFilter}
          fixedWidth="250px"
          onItemRender={(option) => {
            if (!option.label || option.label === "All Authors") {
              return option.label
            }
            
            // Parse author to separate name and email
            const parts = option.label.split(' ')
            const emailPattern = /\S+@\S+\.\S+/
            const emailIndex = parts.findIndex(part => emailPattern.test(part))
            
            if (emailIndex !== -1) {
              const name = parts.slice(0, emailIndex).join(' ')
              const email = parts[emailIndex]
              
              // For dropdown options, show name and email
              return (
                <div className="flex flex-col py-1">
                  <Typography 
                    className="text-admin_text font-medium">
                    {name || email.split('@')[0]}
                  </Typography>
                  
                  <Typography 
                    variant="admin_txt2_dim"
                    className="opacity-60"
                  >
                    {email}
                  </Typography>
                </div>
              )
            }
            
            return option.label
          }}
          onValueItemRender={(option) => {
            if (!option || !option.label || option.label === "All Authors") {
              return (
                <div className="text-sm font-medium text-admin_text">
                  {option?.label || "All Authors"}
                </div>
              )
            }
            
            // Parse author to separate name and email
            const parts = option.label.split(' ')
            const emailPattern = /\S+@\S+\.\S+/
            const emailIndex = parts.findIndex(part => emailPattern.test(part))
            
            if (emailIndex !== -1) {
              const name = parts.slice(0, emailIndex).join(' ')
              const email = parts[emailIndex]
              
              // For selected value display, show only name with proper styling
              return (
                <div className="text-sm font-medium text-admin_text">
                  {name || email.split('@')[0]}
                </div>
              )
            }
            
            return (
              <div className="text-sm font-medium text-admin_text">
                {option.label}
              </div>
            )
          }}
        />
      </div>
    </div>
  )

  // Table columns
  const columns = [
    {
      id: "address",
      title: "Address",
      headerClassName: "w-[55%]",
      render: (_, item) => {
        if (item.type === 'group') {
          // Render group header
          const indentLevel = item.level * 20
          return (
            <div 
              className="flex items-center border-l-4 border-admin_primary py-2 px-3"
              style={{ marginLeft: `${indentLevel}px` }}
            >
              <div className="flex items-center space-x-2 flex-1">
                {item.isCollapsed ? (
                  <ChevronRightIcon className="w-4 h-4 text-admin_text_secondary" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4 text-admin_text_secondary" />
                )}

                <Typography 
                  variant="admin_txt1" 
                  className="text-admin_text font-semibold"
                >
                  {item.name}
                </Typography>

                <Typography 
                  variant="admin_txt2_dim"
                  className="bg-admin_neutral_1 rounded px-2 py-1"
                >
                  {item.paramCount} param{item.paramCount !== 1 ? 's' : ''}
                </Typography>
              </div>
            </div>
          )
        } else {
          // Render parameter
          const param = item.param
          const type = GetValueType(param.value)
          const indentLevel = item.level * 20
          const displayAddress = item.displayAddress || param.address
          
          return (
            <div style={{ marginLeft: `${indentLevel}px` }}>
              <div className="flex flex-col">
                <div className="flex items-center justify-between pr-4 gap-2">
                  <Typography 
                    variant="admin_txt1" 
                    className="text-admin_text font-medium cursor-pointer hover:text-admin_primary truncate transition-colors"
                    onClick={() => handleEdit(param)}
                  >
                    {displayAddress}
                  </Typography>

                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    type === 'string' ? 'bg-admin_success text-white' :
                    type === 'number' ? 'bg-admin_info_bg text-admin_text' :
                    type === 'boolean' ? 'bg-admin_progress text-white' :
                    type === 'object' ? 'bg-admin_warning text-white' :
                    type === 'array' ? 'bg-admin_primary/50 text-white' :
                    'bg-admin_neutral_1 text-admin_text'
                  }`}>
                    {type}
                  </span>
                </div>

                {param.description && (
                  <Tooltip content={param.description}>
                    <Typography 
                      variant="admin_txt2_dim" 
                      className="mt-1"
                    >
                      {param.description.length > 50
                        ? `${param.description.substring(0, 50)}...`
                        : param.description}
                    </Typography>
                  </Tooltip>
                )}
              </div>
            </div>
          )
        }
      }
    },
    {
      id: "value",
      title: "Value",
      headerClassName: "w-[27%]",
      render: (_, item) => {
        if (item.type === 'group') {
          return (
            <div className="py-2 px-3">
              <span></span>
            </div>
          )
        }
        
        const param = item.param
        const displayValue = FormatValue(param.value)
        return (
          <Typography 
            variant="admin_txt2_dim" 
            className="block font-mono truncate cursor-pointer hover:text-admin_primary transition-colors"
            onClick={() => handleCopyValue(param)}
            title="Click to copy value"
          >
            {displayValue}
          </Typography>
        )
      }
    },
    {
      id: "author",
      title: "Author",
      headerClassName: "w-[18%]",
      render: (_, item) => {
        if (item.type === 'group') {
          return (
            <div className="py-2 px-3">
              <span></span>
            </div>
          )
        }
        
        const param = item.param
        const currentAuthor = param.author || "Unknown"
        const historicalAuthors = getHistoricalAuthors(param)
        const additionalCount = historicalAuthors.length

        // Parse author to separate name and email
        const parseAuthor = (authorString) => {
          if (!authorString || authorString === "Unknown") {
            return { name: "Unknown", email: null }
          }
          
          // Try to split by space and find email pattern
          const parts = authorString.split(' ')
          const emailPattern = /\S+@\S+\.\S+/
          const emailIndex = parts.findIndex(part => emailPattern.test(part))
          
          if (emailIndex !== -1) {
            const name = parts.slice(0, emailIndex).join(' ')
            const email = parts[emailIndex]
            return { name: name || email.split('@')[0], email }
          }
          
          // If no email found, treat entire string as name
          return { name: authorString, email: null }
        }

        const { name, email } = parseAuthor(currentAuthor)

        return (
          <div className="flex flex-col">
            <Typography 
              variant="admin_txt1" 
              className="text-admin_text font-medium"
            >
              {name}
            </Typography>

            {email && (
              <Typography 
                variant="admin_txt2_dim" 
                className="opacity-60"
              >
                {email}
              </Typography>
            )}

            {additionalCount > 0 && (
              <Tooltip 
                content={
                  <div className="text-left">
                    <div className="font-medium mb-1">Additional Authors:</div>
                    {historicalAuthors.map((author, index) => (
                      <div key={index} className="text-sm">
                        {author}
                      </div>
                    ))}
                  </div>
                }
              >
                <Typography 
                  variant="admin_txt2_dim" 
                  className="cursor-help mt-1"
                >
                  +{additionalCount} author{additionalCount !== 1 ? 's' : ''}
                </Typography>
              </Tooltip>
            )}
          </div>
        )
      }
    }
  ]

  return (
    <div className="p-6">
      <div className="w-full flex items-center justify-between mb-5">
        <Typography 
          component="h1"
          variant="admin_h3"
        >
          Parameters
        </Typography>

        <Button
          variant="admin_primary"
          onClick={() => setShowCreateDialog(true)}
          className="gap-1"
        >
          <PlusCircleIcon className="w-5 h-5" />
          Add
        </Button>
      </div>

      <DataTable
        variant="admin_browser"
        apiMethod={GetParams}
        apiDataField="data"
        requestFields={getRequestFields()}
        refreshKey={refreshKey}
        headerControls={headerControls}
        localUserPreferencesKey="admin.paramIO.browser"
        renders={columns}
        preferredHeight="70vh"
        itemHeight="h-[56px]"
        itemsPerPage={50}
        showSearch={true}
        searchField="address value description"
        initialSearchTerm={searchTerm}
        onSearchTermChange={handleSearchChange}
        styleOverrides={{
          table: "w-full table-fixed",
          header: "w-full h-4 flex justify-between items-end gap-2 mt-8 mb-4",
          searchInput: "absolute top-6 right-4 text-text bg-transparent outline-none flex-1 w-[350px] h-[30px] px-2 py-1",
          headerControls: "w-full flex -mt-12",
          getRowStyle: (_, item) => {
            if (item && item.type === 'group') {
              return "bg-admin_paper hover:bg-admin_neutral cursor-pointer"
            }
            return ""
          },
          onRowClick: (item) => {
            if (item && item.type === 'group') {
              toggleGroup(item.fullPath)
            }
          }
        }}
        foldingLogic={{
          processData: (data, search) => buildHierarchicalData(data, search),
          expandedGroups,
          toggleGroup,
          searchTerm
        }}
        noData={
          <div className="text-center py-8">
            <PlusCircleIcon className="w-12 h-12 text-admin_text_secondary mx-auto mb-4" />

            <Typography
              variant="admin_body_dim"
            >
              No parameters found
            </Typography>

            <Button
              variant="admin_secondary"
              className="mt-4"
              onClick={() => setShowCreateDialog(true)}
            >
              Create First Parameter
            </Button>
          </div>
        }
      />

      {/* Create Dialog */}
      {showCreateDialog && (
        <ParamEditDialog
          mode="create"
          onClose={handleDialogClose}
          onRefresh={handleRefresh}
        />
      )}

      {/* Edit Dialog */}
      {showEditDialog && selectedParam && (
        <ParamEditDialog
          param={selectedParam}
          mode="edit"
          onClose={handleDialogClose}
          onRefresh={handleRefresh}
        />
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && selectedParam && (
        <Modal
          variant="admin"
          title="Delete Parameter"
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
        >
          <div className="space-y-4">
            <Typography 
              variant="admin_txt1" 
              className="text-admin_text"
            >
              Are you sure you want to delete the parameter "{selectedParam.address}"?
            </Typography>

            <Typography variant="admin_txt2_dim">
              This action cannot be undone. All history for this parameter will also be deleted.
            </Typography>

            <div className="flex items-center justify-end space-x-3 pt-4">
              <Button
                variant="admin_secondary"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </Button>

              <Button
                variant="admin_error"
                onClick={confirmDelete}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default ParamBrowser